import { Chart } from "primereact/chart";
import { useEffect, useRef, useState } from "react";
import { formatNumberID } from "../../../../../utils/FormatNumber";
import { format } from "date-fns";
import { Skeleton } from "primereact/skeleton";

const legendColors = [
  // Secondary colors (good contrast)
  "from-green-600 to-emerald-950",
  "from-gray-500 to-gray-700",
  "from-yellow-500 to-orange-500",
];

const colors = [
  // Secondary colors (good contrast)
  "#076e2eff",
  "#6b7280",
  "#f59e0b",
];

const OwnerDashboardCustomerPieChart = (props) => {
  const { data, filterValue, loading } = props;

  const loyaltyPieRef = useRef(null);

  const [loyaltyData, setLoyaltyData] = useState({});
  const [loyaltyOptions, setLoyaltyOptions] = useState({});

  const generateDoughnutChart = () => {
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 10, // 👈 Add padding inside the canvas so hoverOffset doesn't clip
      },
      plugins: {
        legend: {
          display: false,
        },
      },
    };

    const loyaltyData = {
      labels: data?.customer_loyalty_levels?.map((item) => item.level_name),
      datasets: [
        {
          data: data?.customer_loyalty_levels?.map((item) => item.total),
          backgroundColor: colors,
          hoverOffset: 20,
          hoverBorderColor: "#ffffff",
          hoverBorderWidth: 2,
        },
      ],
    };

    setLoyaltyData(loyaltyData);
    setLoyaltyOptions(options);
  };

  useEffect(() => {
    // console.log(data);
    if (data) generateDoughnutChart();
  }, [data]);

  return (
    <div className="flex flex-col lg:flex-row  gap-4 w-full">
      {/* Pie Chart Section */}
      <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 border border-gray-300">
        {/* Header */}
        <div className="p-6 col-center-start gap-0.5 !pb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-black">
            Customer Loyalty Levels
          </h2>
          <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
            Lifetime
          </span>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="space-y-6 animate-pulse w-full">
              {/* Chart Placeholder */}
              <div className="flex justify-center">
                <div className="w-[90%] max-w-md lg:w-80 aspect-square">
                  <Skeleton width="100%" height="100%" shape="circle" />
                </div>
              </div>

              {/* Legend Skeleton */}
              <div className="space-y-4 max-h-80 overflow-y-auto w-full">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg w-full"
                  >
                    {/* Left side */}
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton width="2rem" height="1rem" borderRadius="4px" />
                      <div className="flex flex-col gap-1 w-full">
                        <Skeleton width="50%" height="1rem" />
                        <Skeleton width="33%" height="0.75rem" />
                      </div>
                    </div>

                    {/* Right side (Amount) */}
                    <Skeleton width="5rem" height="1rem" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row  gap-6">
              {/* Chart Section */}
              <div className="flex-shrink-0 flex justify-center">
                <div className="w-full max-w-md lg:w-[80rem] row-center-center">
                  <Chart
                    ref={loyaltyPieRef}
                    type="doughnut"
                    data={loyaltyData}
                    options={loyaltyOptions}
                    className="w-[90%] aspect-square"
                  />
                </div>
              </div>

              {/* Legend Section */}
              <div className="flex-1 min-w-0 flex flex-col items-between justify-center">
                <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {data?.customer_loyalty_levels?.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      onMouseEnter={() => {
                        const chart = loyaltyPieRef.current?.getChart?.();
                        if (chart) {
                          chart.setActiveElements([{ datasetIndex: 0, index }]);
                          chart.tooltip.setActiveElements(
                            [{ datasetIndex: 0, index }],
                            { x: 0, y: 0 }
                          );
                          chart.update();
                        }
                      }}
                      onMouseLeave={() => {
                        const chart = loyaltyPieRef.current?.getChart?.();
                        if (chart) {
                          chart.setActiveElements([]);
                          chart.tooltip.setActiveElements([], { x: 0, y: 0 });
                          chart.update();
                        }
                      }}
                    >
                      {/* Left side - Method info */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className={`w-7 h-3 sm:w-8 sm:h-4 rounded-sm border border-black flex-shrink-0 bg-gradient-to-br ${
                            legendColors[index % legendColors.length]
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-black text-sm sm:text-base truncate">
                            {item.level_name}
                          </p>
                        </div>
                      </div>

                      {/* Right side - Amount */}
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="font-bold text-black text-sm sm:text-base">
                          {formatNumberID(item.total)} Members
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboardCustomerPieChart;
